/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointUpgradeBannerComponent" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Details/Resources";
import { ServiceEndpointActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsCreator";
import { IServiceEndpointComponentState, ConversionStatus, ServiceEndpointStore } from "DistributedTaskUI/ServiceEndpoints/Details/Stores/ServiceEndpointStore";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { IMessageCardProps, MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { Link } from "VSSUI/Link";
import { Dialog } from "VSSUI/Dialog";
import { DataSourceDetails, ResultTransformationDetails, ServiceEndpoint, ServiceEndpointRequest, ServiceEndpointRequestResult } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { ServiceEndpointsSource } from "../Source/ServiceEndpointsSource";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ServiceEntpointFeatureFlags } from "../Constants";

export interface IServiceEndpointUpgradeBannerComponentProps {}

interface IServiceEndpointUpgradeBannerComponentState extends IServiceEndpointComponentState {
    isConfirmationDialogueOpen: boolean;
    isSecretExpiredLoading: boolean;
}

export class ServiceEndpointUpgradeBannerComponent extends VssComponent<IServiceEndpointUpgradeBannerComponentProps, IServiceEndpointUpgradeBannerComponentState> {
    constructor(props: IServiceEndpointUpgradeBannerComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._actionCreator = ActionCreatorManager.GetActionCreator<ServiceEndpointActionsCreator>(
            ServiceEndpointActionsCreator,
            "service-endpoints-section-actionscreator"
        );
        this._store = StoreManager.GetStore<ServiceEndpointStore>(ServiceEndpointStore, ServiceEndpointStore.getKey());
        this._source = ServiceEndpointsSource.getInstance(context.pageContext);
        this._isSecretExpirationBannerFeatureEnabled = isFeatureFlagEnabled(this.context.pageContext, ServiceEntpointFeatureFlags.EnableSecretExpirationBanner);
        this.state = {
            ...ServiceEndpointStore.getDefaultState(),
            isConfirmationDialogueOpen: false,
            isSecretExpiredLoading: this._isSecretExpirationBannerFeatureEnabled
        };
    }

    public UNSAFE_componentWillMount(): void {
        this.setState({
            ...this._store.getState()
        });
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._onStoreChanged);

        if (this._isSecretExpirationBannerFeatureEnabled) {
            this._verifyServiceEndpointExpired().then((result) => {
                const expiredSecretErrorCode = "AADSTS7000222";
                this._isServiceEndpointSecretExpired.value = result.errorMessage.indexOf(expiredSecretErrorCode) >= 0;
                this.setState({ isSecretExpiredLoading: false });
            });
        }
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
    }

    private _onStoreChanged = (): void => {
        this.setState({...this._store.getState()});
        if (this.state.conversionStatus === ConversionStatus.SUCCESSFUL) {
            this._showSuccessToast();
        }
    }

    private _showSuccessToast = () => {
        const messagesService: IGlobalMessagesService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        messagesService.addToast({
            message: Resources.ARMServiceConnectionEndpointSchemeConversionSuccessful,
            duration: 10000,
            forceOverrideExisting: true
        });
    }

    
    public render(): React.ReactNode {
        if (this.state.isSecretExpiredLoading) {
            return null;
        }
        return (
            <React.Fragment>
                <Observer conversionStatus={this.state.conversionStatus} isServiceEndpointSecretExpired={this._isServiceEndpointSecretExpired}>
                    {(observedProps: { conversionStatus: ConversionStatus; isServiceEndpointSecretExpired: boolean }) => {
                        const messageCardProps = this._getMessageCardProps(observedProps.conversionStatus, this.state.serviceEndpoint, observedProps.isServiceEndpointSecretExpired);
                        return messageCardProps && <MessageCard className="flex-self-stretch margin-bottom-16" {...messageCardProps} />;
                    }}
                </Observer>
                {this.state.isConfirmationDialogueOpen && this._renderConfirmationDialog()}
            </React.Fragment>
        );
    }

    private _verifyServiceEndpointExpired(): Promise<ServiceEndpointRequestResult> {
        const serviceEndpointRequest: ServiceEndpointRequest = this._getServiceEndpointRequestForTest(this.state.serviceEndpoint);
        return this._source.verifyServiceEndpoint(serviceEndpointRequest, this.state.serviceEndpoint.id);
    }

    private _getServiceEndpointRequestForTest(serviceEndpoint: ServiceEndpoint): ServiceEndpointRequest {
        const dataSourceDetails: DataSourceDetails = {
            dataSourceName: "TestConnection",
            dataSourceUrl: "",
            headers: null,
            resourceUrl: "",
            requestContent: null,
            requestVerb: null,
            parameters: null,
            resultSelector: "",
            initialContextTemplate: ""
        };

        const resultTransformationDetails: ResultTransformationDetails = {
            callbackContextTemplate: "",
            callbackRequiredTemplate: "",
            resultTemplate: ""
        };

        return {
            dataSourceDetails: dataSourceDetails,
            resultTransformationDetails: resultTransformationDetails,
            serviceEndpointDetails: serviceEndpoint
        };
    }

    private _getMessageCardProps = (
        conversionStatus: ConversionStatus,
        serviceEndpoint: ServiceEndpoint,
        isServiceEndpointSecretExpired: boolean
    ): React.PropsWithChildren<IMessageCardProps> => {
        switch (conversionStatus) {
            case ConversionStatus.AVAILABLE: {
                const baseProps = {
                    iconProps: { iconName: isServiceEndpointSecretExpired ? "ReportHacked" : "Shield" },
                    severity: isServiceEndpointSecretExpired ? MessageCardSeverity.Error : undefined,
                    buttonProps: [
                        {
                            text: Resources.ARMServiceConnectionUpgradeAvailableButton,
                            onClick: this._openConfirmationDialog
                        }
                    ]
                };

                const messagePrefix = isServiceEndpointSecretExpired ? `${Resources.ARMServiceConnectionSecretExpired} ` : "";
                const message =
                    serviceEndpoint.data.creationMode === "Manual"
                        ? Resources.ARMManualServiceConnectionConversion
                        : Resources.ARMServiceConnectionUpgradeAvailable;
                const url =
                    serviceEndpoint.data.creationMode === "Manual"
                        ? "https://aka.ms/azdo-rm-workload-identity-manual"
                        : "https://aka.ms/azdo-rm-workload-identity";

                return {
                    ...baseProps,
                    children: this._renderBannerMessage(`${messagePrefix}${message}`, url)
                };
            }
            case ConversionStatus.IN_PROGRESS:
                return {
                    children: Resources.ARMServiceConnectionUpgradeInProgress
                };
            case ConversionStatus.FAILED:
                return {
                    severity: MessageCardSeverity.Warning,
                    buttonProps: [
                        {
                            text: Resources.ARMServiceConnectionUpgradeFailedButton,
                            onClick: () =>
                                this._actionCreator.convertServiceEndpointAuthenticationScheme(serviceEndpoint, this.state.eligibleSchemaUpgrade)
                        }
                    ],
                    children: this._renderBannerMessage(Resources.ARMServiceConnectionUpgradeFailed, "https://aka.ms/azdo-rm-workload-identity")
                };
            default:
                return null;
        }
    }

    private _renderBannerMessage = (message: string, url: string) =>
    <div>
        <span>{message}</span>&nbsp;
        <Link target="_blank" href={url}>{Resources.LearnMoreText}</Link>
    </div>

    private _openConfirmationDialog = () => this.setState({
        isConfirmationDialogueOpen: true
    })

    private _renderConfirmationDialog = () => {
        return <Dialog
            defaultActiveElement={".bolt-checkbox-label"}
            calloutContentClassName="service-endpoint-conversion-dialog"
            footerButtonProps={[
                {
                    onClick: () => {
                        this._closeConfirmDialog(false);
                    },
                    text: Resources.Cancel
                },
                {
                    primary: true,
                    onClick: () => {
                        this._closeConfirmDialog(true);
                    },
                    text: Resources.Convert
                }
            ]}
            lightDismiss={true}
            onDismiss={() => {
                this._closeConfirmDialog(false);
            }}
            titleProps={{ text: Resources.ConfirmConversionDialogueTitle }}
        >
            <div className="margin-bottom-8">
                <span>{Resources.ConfirmConversionDialogueMessage}</span>&nbsp;
                <Link target="_blank" href="https://aka.ms/azdo-rm-secret-deletion">{Resources.LearnMoreText}</Link>
            </div>
        </Dialog>;
    }

    private _closeConfirmDialog(decision: boolean) {
        this.setState({ isConfirmationDialogueOpen: false });

        if (decision) {
            this._actionCreator.convertServiceEndpointAuthenticationScheme(this.state.serviceEndpoint, this.state.eligibleSchemaUpgrade);
        }
    }

    private _actionCreator: ServiceEndpointActionsCreator;
    private _store: ServiceEndpointStore;
    private readonly _isServiceEndpointSecretExpired: IObservableValue<boolean> = new ObservableValue(false);
    private _source: ServiceEndpointsSource;
    private _isSecretExpirationBannerFeatureEnabled: boolean;
}
